import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { TEXT_OPERATORS, NUMERIC_OPERATORS, DATE_OPERATORS } from '../../../hooks/useFilterTemplate';

const FilterTemplateDropdown = (props) => {
  const {
    columnKey,
    columnLabel,
    showTextFilters = true,
    // Hide the "Select Values" checkbox tab (e.g. server-paginated grids where a
    // column has too many distinct values to enumerate). Advanced tab only.
    showValuesTab = true,
    isNumericColumn = false,
    isDateColumn = false,
    columnFilters,
    filterMode,
    filterText,
    filterSearch,
    filterButtonPositions,
    setOpenFilterColumn,
    setFilterSearch,
    getUniqueValues,
    handleFilterToggle,
    selectAllColumnFilter,
    clearColumnFilter,
    applyTextFilter,
  } = props;
  const dropdownRef = useRef(null);
  const [activeTab, setActiveTab] = useState(showValuesTab ? 'values' : 'advanced');
  const [numericOperator, setNumericOperator] = useState('gte');
  const [numericValue, setNumericValue] = useState('');
  const [textOperator, setTextOperator] = useState('contains');
  const [dateOperator, setDateOperator] = useState('dateOnOrAfter');
  const [dateValue, setDateValue] = useState('');

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') { setOpenFilterColumn(null); setFilterSearch(''); } };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [setOpenFilterColumn, setFilterSearch]);

  useEffect(() => {
    const h = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenFilterColumn(null);
        setFilterSearch('');
      }
    };
    const t = setTimeout(() => document.addEventListener('mousedown', h), 100);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', h); };
  }, [setOpenFilterColumn, setFilterSearch]);

  const handleApplyAdvancedFilter = () => {
    if (isDateColumn) {
      if (['empty', 'notEmpty'].includes(dateOperator)) {
        applyTextFilter(columnKey, dateOperator, '');
      } else if (dateValue) {
        applyTextFilter(columnKey, dateOperator, dateValue);
      }
    } else if (isNumericColumn && numericValue !== '') {
      applyTextFilter(columnKey, numericOperator, numericValue);
    } else if (['empty', 'notEmpty'].includes(textOperator)) {
      applyTextFilter(columnKey, textOperator, '');
    } else if (filterSearch.trim()) {
      applyTextFilter(columnKey, textOperator, filterSearch);
    }
  };

  const tabStyle = (isActive) => ({
    flex: 1,
    padding: '8px 10px',
    fontSize: '11px',
    fontWeight: '600',
    backgroundColor: isActive ? 'white' : '#f1f3f4',
    border: 'none',
    borderBottom: isActive ? '2px solid #007bff' : '2px solid transparent',
    cursor: 'pointer',
    color: isActive ? '#007bff' : '#5f6368',
  });

  const pos = filterButtonPositions[columnKey] || { top: 100, left: 100 };
  const label = columnLabel || columnKey;

  const panelStyle = {
    position: 'fixed',
    top: pos.top,
    left: Math.min(pos.left, window.innerWidth - 320),
    backgroundColor: 'white',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    zIndex: 99999,
    width: '300px',
    maxHeight: '480px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    color: '#333',
  };

  const content = (
    <div ref={dropdownRef} className="filter-dropdown-portal filter-template-dropdown" style={panelStyle} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: '10px 12px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8f9fa' }}>
        <span style={{ fontSize: '13px', fontWeight: '600' }}>Filter: {label}</span>
        <button type="button" onClick={() => { setOpenFilterColumn(null); setFilterSearch(''); }} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#666', padding: '0 4px', lineHeight: 1 }}>×</button>
      </div>
      {showTextFilters && showValuesTab && (
        <div style={{ display: 'flex', borderBottom: '1px solid #eee' }}>
          <button type="button" onClick={() => setActiveTab('values')} style={tabStyle(activeTab === 'values')}>Select Values</button>
          <button type="button" onClick={() => setActiveTab('advanced')} style={tabStyle(activeTab === 'advanced')}>Advanced{filterText[columnKey] ? ' ●' : ''}</button>
        </div>
      )}
      {(!showTextFilters || activeTab === 'values') && showValuesTab ? (
        <>
          <div style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
            <input type="text" placeholder="Search values..." value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (filterSearch.trim()) applyTextFilter(columnKey, 'contains', filterSearch); } }} style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box' }} autoFocus />
          </div>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #eee', display: 'flex', gap: '8px' }}>
            <button type="button" onClick={() => selectAllColumnFilter(columnKey)} style={{ padding: '5px 10px', fontSize: '11px', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#28a745', color: 'white', fontWeight: '500' }}>Select All</button>
            <button type="button" onClick={() => clearColumnFilter(columnKey)} style={{ padding: '5px 10px', fontSize: '11px', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#dc3545', color: 'white', fontWeight: '500' }}>Clear</button>
          </div>
          {(columnFilters[columnKey] && columnFilters[columnKey].length > 0) || filterText[columnKey] ? (
            <div style={{ padding: '6px 10px', backgroundColor: '#d1fae5', borderBottom: '1px solid #eee', fontSize: '11px', color: '#065f46' }}>
              Active: {columnFilters[columnKey] && columnFilters[columnKey].length > 0 ? columnFilters[columnKey].length + ' values' : ''} {filterText[columnKey] ? filterMode[columnKey] + ' "' + filterText[columnKey] + '"' : ''}
            </div>
          ) : null}
          <div style={{ padding: '4px 0', maxHeight: '200px', overflow: 'auto', flex: '1 1 auto' }}>
            {getUniqueValues(columnKey).filter(val => !filterSearch || String(val).toLowerCase().includes(filterSearch.toLowerCase())).slice(0, 100).map(val => {
              const selected = columnFilters[columnKey];
              const isChecked = selected === undefined ? true : (selected.length === 0 ? false : selected.includes(val));
              return (
                <label key={String(val)} style={{ display: 'flex', alignItems: 'center', padding: '6px 10px', cursor: 'pointer', fontSize: '12px', backgroundColor: isChecked ? '#e8f5e9' : 'transparent' }}>
                  <input type="checkbox" checked={isChecked} onChange={() => handleFilterToggle(columnKey, val)} style={{ marginRight: '10px', accentColor: '#28a745' }} />
                  {val || '(empty)'}
                </label>
              );
            })}
          </div>
        </>
      ) : (
        <div style={{ padding: '12px', flex: 1, overflow: 'auto' }}>
          {isDateColumn ? (
            <>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Condition</label>
                <select value={dateOperator} onChange={(e) => setDateOperator(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                  {DATE_OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                </select>
              </div>
              {!['empty', 'notEmpty'].includes(dateOperator) && (
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Date</label>
                  <input type="date" value={dateValue} onChange={(e) => setDateValue(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }} />
                </div>
              )}
            </>
          ) : isNumericColumn ? (
            <>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Condition</label>
                <select value={numericOperator} onChange={(e) => setNumericOperator(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                  {NUMERIC_OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Value</label>
                <input type="number" value={numericValue} onChange={(e) => setNumericValue(e.target.value)} placeholder="Enter number..." style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }} />
              </div>
            </>
          ) : (
            <>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Condition</label>
                <select value={textOperator} onChange={(e) => setTextOperator(e.target.value)} style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                  {TEXT_OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                </select>
              </div>
              {!['empty', 'notEmpty'].includes(textOperator) && (
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Value</label>
                  <input type="text" value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} placeholder="Enter text..." style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }} onKeyDown={(e) => { if (e.key === 'Enter') handleApplyAdvancedFilter(); }} />
                </div>
              )}
            </>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button type="button" onClick={handleApplyAdvancedFilter} style={{ flex: 1, padding: '8px', fontSize: '12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>Apply</button>
            <button type="button" onClick={() => clearColumnFilter(columnKey)} style={{ flex: 1, padding: '8px', fontSize: '12px', backgroundColor: '#f1f3f4', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Clear</button>
          </div>
          {filterText[columnKey] && (
            <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#d1fae5', borderRadius: '4px', fontSize: '11px', color: '#065f46' }}>
              Active: {filterMode[columnKey]} "{filterText[columnKey]}"
            </div>
          )}
        </div>
      )}
      <div style={{ padding: '10px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', backgroundColor: '#f8f9fa' }}>
        <button type="button" onClick={() => { if (filterSearch.trim()) applyTextFilter(columnKey, 'contains', filterSearch); setOpenFilterColumn(null); setFilterSearch(''); }} style={{ padding: '6px 16px', fontSize: '12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500' }}>Done</button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default FilterTemplateDropdown;
